import { useEffect, useSyncExternalStore } from 'react'
import type { Ik, Mens, Project, Taak } from '../../gedeeld/types'
import { api } from './api'

/* ===========================================================================
 *  Wat de hele app deelt
 *
 *  Een kleine winkel zonder bibliotheek: een object, een lijst luisteraars,
 *  en useSyncExternalStore. Wat erin staat is wat bijna elk scherm nodig
 *  heeft -- wie je bent, wie er werken, welke projecten er zijn -- en wat
 *  dus niet op elk scherm opnieuw opgehaald hoort te worden.
 *
 *  De taken staan er NIET in. Die horen bij een scherm (het bord van één
 *  project, mijn lijst) en worden daar opgehaald; een winkel met alle taken
 *  van alle projecten is een winkel die altijd een beetje oud is.
 *  Behalve: een taak die ergens wordt opgeslagen, wordt hier aangekondigd
 *  (taakGewijzigd), zodat elk open scherm hem kan bijwerken zonder opnieuw
 *  te vragen.
 * =========================================================================== */

interface Staat {
  ik: Ik | null
  github: { ingesteld: boolean; org: string }
  mensen: Mens[]
  projecten: Project[]
  projectenGeladen: boolean
  meldingen: { id: number; soort: 'ok' | 'fout' | 'info'; tekst: string }[]
  /** De taak die als lade over het scherm ligt. */
  openTaak: string | null
  zoekOpen: boolean
  nieuweTaak: { projectId?: string | null; status?: string; sprintId?: string | null } | null
}

let staat: Staat = {
  ik: null,
  github: { ingesteld: false, org: '' },
  mensen: [],
  projecten: [],
  projectenGeladen: false,
  meldingen: [],
  openTaak: null,
  zoekOpen: false,
  nieuweTaak: null,
}

const luisteraars = new Set<() => void>()

export function zet(deel: Partial<Staat>) {
  staat = { ...staat, ...deel }
  for (const l of luisteraars) l()
}

export function lees(): Staat { return staat }

function abonneer(l: () => void) {
  luisteraars.add(l)
  return () => { luisteraars.delete(l) }
}

export function useStaat<T>(kies: (s: Staat) => T): T {
  return useSyncExternalStore(abonneer, () => kies(staat))
}

/* ------------------------------------------------------------------ *
 *  Meldingen in beeld
 * ------------------------------------------------------------------ */

let volgnummer = 0

export function meld(tekst: string, soort: 'ok' | 'fout' | 'info' = 'ok') {
  const id = ++volgnummer
  zet({ meldingen: [...staat.meldingen, { id, soort, tekst }].slice(-4) })
  /* Een fout blijft langer staan: die moet je kunnen lezen. */
  setTimeout(() => zet({ meldingen: staat.meldingen.filter((m) => m.id !== id) }), soort === 'fout' ? 8000 : 3200)
}

export const meldFout = (e: unknown) => meld(e instanceof Error ? e.message : String(e), 'fout')

/* ------------------------------------------------------------------ *
 *  Mensen en projecten
 * ------------------------------------------------------------------ */

let mensenBezig: Promise<void> | null = null

export function laadMensen(opnieuw = false): Promise<void> {
  if (mensenBezig && !opnieuw) return mensenBezig
  mensenBezig = api.mensen()
    .then((mensen) => zet({ mensen }))
    .catch((e) => { mensenBezig = null; meldFout(e) })
  return mensenBezig
}

export async function laadProjecten(): Promise<void> {
  try {
    zet({ projecten: await api.projecten(true), projectenGeladen: true })
  } catch (e) {
    zet({ projectenGeladen: true })
    meldFout(e)
  }
}

/** Mensen en projecten, geladen bij het eerste scherm dat ze vraagt. */
export function useBasis() {
  const mensen = useStaat((s) => s.mensen)
  const projecten = useStaat((s) => s.projecten)
  const geladen = useStaat((s) => s.projectenGeladen)
  useEffect(() => {
    void laadMensen()
    if (!lees().projectenGeladen) void laadProjecten()
  }, [])
  return { mensen, projecten, geladen }
}

export function projectBijwerken(p: Project) {
  const er = staat.projecten.some((x) => x.id === p.id)
  zet({
    projecten: er
      ? staat.projecten.map((x) => (x.id === p.id ? { ...x, ...p, telling: x.telling, leden: x.leden, sprintActief: x.sprintActief } : x))
      : [...staat.projecten, p],
  })
}

/* ------------------------------------------------------------------ *
 *  Een taak veranderde ergens
 * ------------------------------------------------------------------ */

type TaakLuisteraar = (t: Taak | { id: string; weg: true }) => void
const taakLuisteraars = new Set<TaakLuisteraar>()

export function taakGewijzigd(t: Taak | { id: string; weg: true }) {
  for (const l of taakLuisteraars) l(t)
}

/** Houdt een lijst taken bij als er elders een verandert. */
export function useTaakWijzigingen(f: TaakLuisteraar) {
  useEffect(() => {
    taakLuisteraars.add(f)
    return () => { taakLuisteraars.delete(f) }
  }, [f])
}

/** Een lijst bijwerken met één gewijzigde taak. */
export function verwerk(lijst: Taak[], t: Taak | { id: string; weg: true }, hoortErbij?: (t: Taak) => boolean): Taak[] {
  if ('weg' in t) return lijst.filter((x) => x.id !== t.id)
  const er = lijst.some((x) => x.id === t.id)
  if (hoortErbij && !hoortErbij(t)) return er ? lijst.filter((x) => x.id !== t.id) : lijst
  return er ? lijst.map((x) => (x.id === t.id ? t : x)) : [...lijst, t]
}

export const openTaak = (id: string | null) => zet({ openTaak: id })
export const nieuweTaak = (v: Staat['nieuweTaak']) => zet({ nieuweTaak: v })
